import React, { useCallback } from 'react';
import './agents.css';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import { deleteAgent, deleteAllAgents, listAgents } from '../../helpers/agents';

import AgentList from './AgentList';
import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import Chat from './Chat';
import AgentSidebar from './components/AgentSidebar';
import {
  useAgentList,
  useAgentDetail,
  useModelAndMode,
  useMessageComposer,
  useMentionData,
  useSidebarViewers,
  useURLInit,
} from './hooks';

const AgentsPage = () => {
  const {
    agents, setAgents, agentsLoading,
    selectedAgentId, setSelectedAgentId,
    creatingNewAgent, setCreatingNewAgent,
    loadAgents,
    deleteAllConfirmOpen, setDeleteAllConfirmOpen,
    deletingAll, setDeletingAll,
    deleteAgentConfirmId, setDeleteAgentConfirmId,
    deletingAgent, setDeletingAgent,
  } = useAgentList();

  const {
    agentDetail, setAgentDetail,
    detailLoading, detailError,
    fetchDetail, setPollingNonce,
    busy, messagesEndRef,
  } = useAgentDetail({ selectedAgentId });

  const {
    availableModels, modelsLoading, selectedModelId, setSelectedModelId,
    availableModes, modesLoading, selectedMode,
    handleModeChange,
  } = useModelAndMode({
    selectedAgentId,
    agentDetailMode: agentDetail?.mode,
  });

  const {
    message, setMessage,
    sending, confirming, creating, creationMessages,
    quotes, cursorPositionRef, chatInputRef,
    documentCropAttachment,
    handleQuote, handleRemoveQuote,
    handleDocumentTextQuote, handleDocumentRegionCrop, handleRemoveCropAttachment,
    handleSendMessage, handleConfirm, handleCreateAgent,
  } = useMessageComposer({
    selectedAgentId,
    selectedModelId,
    fetchDetail,
    setPollingNonce,
    setAgents,
    setSelectedAgentId,
    setCreatingNewAgent,
  });

  const { onboardingMode } = useURLInit({
    setMessage,
    setCreatingNewAgent,
    setSelectedAgentId,
    setAgentDetail,
  });

  const {
    policies, policiesLoading, handleLoadPolicies,
    mentionTasks, mentionTasksLoading, mentionTasksHasMore, handleLoadTasks, handleLoadMoreTasks,
    mentionDocuments, mentionDocumentFolders, mentionDocumentsLoading, mentionDocumentBreadcrumbs,
    handleLoadDocuments, handleNavigateDocumentFolder, handleNavigateDocumentRoot,
    mentionFunctions, mentionFunctionsLoading, handleLoadFunctions,
    subscribers, subscribersLoading, handleLoadSubscribers,
  } = useMentionData();

  const {
    sidebarItems,
    handleCloseSidebarItem, handleOpenDocument, handleOpenPolicy, handleOpenTask,
  } = useSidebarViewers();

  // Cross-hook handlers composed here to avoid circular dependencies
  const handleStartNewAgent = useCallback(() => {
    setSelectedAgentId(null);
    setAgentDetail(null);
    setCreatingNewAgent(true);
    setMessage('');
  }, [setSelectedAgentId, setAgentDetail, setCreatingNewAgent, setMessage]);

  const handleDeleteAgent = useCallback(async () => {
    const agentId = deleteAgentConfirmId;
    if (!agentId) return;
    setDeletingAgent(true);
    try {
      await deleteAgent(agentId);
      toast.success('Conversation deleted');
      const refreshed = await listAgents();
      setAgents(refreshed.data || []);
      if (agentId === selectedAgentId) {
        setSelectedAgentId(null);
        setAgentDetail(null);
        setCreatingNewAgent(false);
      }
      setDeleteAgentConfirmId(null);
    } catch {
      toast.error('Failed to delete conversation');
    } finally {
      setDeletingAgent(false);
    }
  }, [deleteAgentConfirmId, selectedAgentId, setAgents, setSelectedAgentId, setAgentDetail, setCreatingNewAgent, setDeleteAgentConfirmId, setDeletingAgent]);

  const handleDeleteAllAgents = useCallback(async () => {
    setDeletingAll(true);
    try {
      await deleteAllAgents();
      toast.success('All conversations deleted');
      setAgents([]);
      setSelectedAgentId(null);
      setAgentDetail(null);
      setCreatingNewAgent(false);
      setDeleteAllConfirmOpen(false);
    } catch {
      toast.error('Failed to delete conversations');
    } finally {
      setDeletingAll(false);
    }
  }, [setAgents, setSelectedAgentId, setAgentDetail, setCreatingNewAgent, setDeleteAllConfirmOpen, setDeletingAll]);

  useDialogKeyboard(!!deleteAgentConfirmId, { onEnter: handleDeleteAgent, onEscape: () => setDeleteAgentConfirmId(null), disabled: deletingAgent });
  useDialogKeyboard(deleteAllConfirmOpen, { onEnter: handleDeleteAllAgents, onEscape: () => setDeleteAllConfirmOpen(false), disabled: deletingAll });

  const chatProps = {
    quotes,
    onQuote: handleQuote,
    onRemoveQuote: handleRemoveQuote,
    cursorPositionRef,
    chatInputRef,
    policies,
    policiesLoading,
    onLoadPolicies: handleLoadPolicies,
    tasks: mentionTasks,
    tasksLoading: mentionTasksLoading,
    hasMoreTasks: mentionTasksHasMore,
    onLoadTasks: handleLoadTasks,
    onLoadMoreTasks: handleLoadMoreTasks,
    documents: mentionDocuments,
    documentFolders: mentionDocumentFolders,
    documentsLoading: mentionDocumentsLoading,
    documentBreadcrumbs: mentionDocumentBreadcrumbs,
    onLoadDocuments: handleLoadDocuments,
    onNavigateDocumentFolder: handleNavigateDocumentFolder,
    onNavigateDocumentRoot: handleNavigateDocumentRoot,
    functions: mentionFunctions,
    functionsLoading: mentionFunctionsLoading,
    onLoadFunctions: handleLoadFunctions,
    subscribers,
    subscribersLoading,
    onLoadSubscribers: handleLoadSubscribers,
    models: availableModels,
    modelsLoading,
    selectedModelId,
    onModelChange: setSelectedModelId,
    modes: availableModes,
    modesLoading,
    selectedMode,
    onModeChange: handleModeChange,
    onOpenDocument: handleOpenDocument,
    documentCropAttachment,
    onRemoveCropAttachment: handleRemoveCropAttachment,
    onOpenPolicy: handleOpenPolicy,
    onOpenTask: handleOpenTask,
  };

  return (
    <Box
      component="main"
      className="MainContent"
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'row',
        bgcolor: 'background.surface',
        overflow: 'hidden',
      }}
    >
      <AgentList
        agents={agents}
        agentsLoading={agentsLoading}
        selectedAgentId={selectedAgentId}
        creatingNewAgent={creatingNewAgent}
        onSelectAgent={(id) => {
          setCreatingNewAgent(false);
          setSelectedAgentId(id);
        }}
        onRefresh={loadAgents}
        onCreateNew={handleStartNewAgent}
        onDeleteAgent={(id) => setDeleteAgentConfirmId(id)}
        onDeleteAll={() => setDeleteAllConfirmOpen(true)}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {onboardingMode && (
          <Alert color="primary" variant="soft" sx={{ m: 2, mb: 0 }}>
            <Box>
              <Typography level="title-sm">Onboarding Step 2 of 2</Typography>
              <Typography level="body-sm">
                Start your first policy by uploading an example PDF using the paperclip button or drag-and-drop, then describe the fields and flow you need.
              </Typography>
            </Box>
          </Alert>
        )}

        {detailLoading && !agentDetail && (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <CircularProgress size="lg" />
            <Typography level="body-sm" color="neutral" sx={{ mt: 2 }}>
              Loading conversation…
            </Typography>
          </Stack>
        )}

        {detailError && (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <Typography level="body-md" color="danger">{detailError}</Typography>
            <Button variant="outlined" onClick={() => fetchDetail(true)} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Stack>
        )}

        {!selectedAgentId && !creatingNewAgent && !detailLoading && (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'primary.softBg',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <AddIcon sx={{ fontSize: 40, color: 'primary.500' }} />
            </Box>
            <Typography level="h3" sx={{ mb: 1 }}>Start a conversation</Typography>
            <Typography level="body-md" color="neutral" sx={{ mb: 3, textAlign: 'center', maxWidth: 400 }}>
              Create a new agent to analyze documents and generate compliance policies.
            </Typography>
            <Button
              size="lg"
              startDecorator={<AddIcon />}
              onClick={handleStartNewAgent}
              sx={{ px: 4, borderRadius: 'xl' }}
            >
              New Conversation
            </Button>
          </Stack>
        )}

        {creatingNewAgent && (
          <Chat
            messages={creationMessages}
            busy={false}
            status="new"
            message={message}
            onMessageChange={setMessage}
            onSend={handleCreateAgent}
            sending={creating}
            onConfirm={() => { }}
            confirming={false}
            messagesEndRef={messagesEndRef}
            isCreationMode
            {...chatProps}
          />
        )}

        {selectedAgentId && agentDetail && (
          <Chat
            messages={agentDetail.messages}
            busy={busy}
            status={agentDetail.status}
            message={message}
            onMessageChange={setMessage}
            onSend={handleSendMessage}
            sending={sending}
            onConfirm={handleConfirm}
            confirming={confirming}
            messagesEndRef={messagesEndRef}
            agentId={selectedAgentId}
            onDeleteChat={() => setDeleteAgentConfirmId(selectedAgentId)}
            {...chatProps}
          />
        )}
      </Box>

      <AgentSidebar
        items={sidebarItems}
        onCloseItem={handleCloseSidebarItem}
        onDocTextQuote={handleDocumentTextQuote}
        onDocRegionCrop={handleDocumentRegionCrop}
        onPolicyQuote={handleQuote}
      />

      <Modal open={!!deleteAgentConfirmId} onClose={() => setDeleteAgentConfirmId(null)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Delete conversation?</DialogTitle>
          <DialogContent>This cannot be undone.</DialogContent>
          <DialogActions>
            <Button color="danger" loading={deletingAgent} onClick={handleDeleteAgent}>
              Delete
            </Button>
            <Button
              variant="plain"
              color="neutral"
              disabled={deletingAgent}
              onClick={() => setDeleteAgentConfirmId(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={deleteAllConfirmOpen} onClose={() => setDeleteAllConfirmOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Delete all conversations?</DialogTitle>
          <DialogContent>This cannot be undone.</DialogContent>
          <DialogActions>
            <Button color="danger" loading={deletingAll} onClick={handleDeleteAllAgents}>
              Delete all
            </Button>
            <Button
              variant="plain"
              color="neutral"
              disabled={deletingAll}
              onClick={() => setDeleteAllConfirmOpen(false)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default AgentsPage;
